import React from 'react';
import { Link } from 'react-router-dom';
import 'iconify-icon';
import { resolveState } from '../../data/brazil-state-centroids';
import { PROGRAM_BY_SLUG, formatDateRange } from './agenda-shared';
import { useGlobalWaLink } from '../../contexts/SettingsContext';

export const TurmaCard = ({ event, franchise }) => {
  const WA_LINK = useGlobalWaLink();
  const program = event.product_id ? PROGRAM_BY_SLUG[event.product_id] : null;
  const modality = (event.modality || event.event_type || '').toLowerCase();
  const seatsRemaining = event.seats_remaining;
  const seatsTotal = event.seats_total;
  const status = event.class_status || 'aberta';
  const isFull = status === 'lotada' || (typeof seatsRemaining === 'number' && seatsRemaining <= 0);
  const isAlmostFull =
    !isFull &&
    typeof seatsRemaining === 'number' &&
    typeof seatsTotal === 'number' &&
    seatsRemaining > 0 &&
    seatsRemaining / seatsTotal <= 0.2;
  const city = event.city || franchise?.city || event.location;
  const evStateCode =
    resolveState(event.state)?.code || (franchise ? resolveState(franchise.state)?.code : null);

  const wpp = (franchise?.whatsapp || franchise?.phone || '').replace(/\D/g, '');
  // Priority: dedicated enrollment form URL > franchise WhatsApp > legacy `link` field > generic WhatsApp
  const ctaLink =
    event.registration_url
    || (wpp ? `https://wa.me/${wpp.length <= 11 ? '55' + wpp : wpp}` : null)
    || event.link
    || WA_LINK;
  const detailsLink = program ? `/${program.slug}` : '/agenda';

  return (
    <article
      className="bg-[#0F0F0F] border border-[#333333] rounded-2xl overflow-hidden hover:border-accent-orange transition-colors flex flex-col"
      data-testid={`turma-card-${event.event_id}`}
    >
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {program && (
            <span
              className="px-3 py-1 bg-accent-orange text-white rounded-full text-[10px] font-bold uppercase tracking-wider"
              data-testid={`turma-program-${program.slug}`}
            >
              {program.name}
            </span>
          )}
          {modality && (
            <span className="px-3 py-1 bg-[#1F1F1F] border border-[#333333] text-neutral-300 rounded-full text-[10px] font-bold uppercase tracking-wider">
              {modality}
            </span>
          )}
          {isFull && (
            <span className="px-3 py-1 bg-red-500/15 border border-red-500/40 text-red-400 rounded-full text-[10px] font-bold uppercase tracking-wider">
              Turma lotada
            </span>
          )}
          {!isFull && isAlmostFull && (
            <span className="px-3 py-1 bg-accent-orange/15 border border-accent-orange/40 text-accent-orange rounded-full text-[10px] font-bold uppercase tracking-wider">
              Últimas vagas
            </span>
          )}
        </div>

        <h3 className="text-xl font-bold text-[#EAEAEA] mb-2 leading-snug">{event.title}</h3>

        <div className="text-sm text-neutral-400 mb-1 flex items-start gap-2">
          <iconify-icon icon="solar:calendar-mark-linear" className="text-accent-orange text-base mt-0.5"></iconify-icon>
          <span>{formatDateRange(event.event_date, event.end_date)}</span>
        </div>
        <div className="text-sm text-neutral-400 mb-1 flex items-start gap-2">
          <iconify-icon icon="solar:clock-circle-linear" className="text-accent-orange text-base mt-0.5"></iconify-icon>
          <span>{event.event_time}</span>
        </div>
        <div className="text-sm text-neutral-400 mb-1 flex items-start gap-2">
          <iconify-icon icon="solar:map-point-linear" className="text-accent-orange text-base mt-0.5"></iconify-icon>
          <span>
            {city}
            {evStateCode && ` · ${evStateCode}`}
          </span>
        </div>

        {franchise && (
          <div className="text-sm text-neutral-400 mb-3 flex items-start gap-2">
            <iconify-icon icon="solar:shop-linear" className="text-accent-orange text-base mt-0.5"></iconify-icon>
            <Link
              to={`/franquia/${franchise.franchise_id}`}
              data-testid={`turma-franchise-${franchise.franchise_id}`}
              className="hover:text-accent-orange transition-colors"
            >
              {franchise.name || `IE ${franchise.city}`}
            </Link>
          </div>
        )}

        {typeof seatsRemaining === 'number' && !isFull && (
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3">
            {seatsRemaining} vaga{seatsRemaining > 1 ? 's' : ''} restante
            {seatsRemaining > 1 ? 's' : ''}
            {typeof seatsTotal === 'number' && ` / ${seatsTotal}`}
          </div>
        )}

        {typeof event.investment === 'number' && event.investment > 0 && (
          <div className="mt-2 mb-4 pb-4 border-b border-[#333333]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-0.5">
              Investimento
            </div>
            <div className="text-2xl font-bold text-[#EAEAEA]">
              R$ {event.investment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">consulte condições especiais</div>
          </div>
        )}

        {event.notes && (
          <p className="text-xs text-neutral-500 italic mt-2 mb-3">{event.notes}</p>
        )}

        <div className="mt-auto flex flex-col sm:flex-row gap-2 pt-2">
          {!isFull ? (
            <a
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`turma-cta-enroll-${event.event_id}`}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-accent-orange text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors"
            >
              <iconify-icon icon="solar:ticket-linear" className="text-base"></iconify-icon>
              Inscreva-se
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#1F1F1F] text-neutral-500 rounded-full text-xs font-bold uppercase tracking-wider cursor-not-allowed"
            >
              Turma lotada
            </button>
          )}
          <Link
            to={detailsLink}
            data-testid={`turma-cta-info-${event.event_id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#333333] text-[#EAEAEA] rounded-full text-xs font-bold uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors"
          >
            Mais informações
            <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
          </Link>
        </div>
      </div>
    </article>
  );
};

export const EmptyState = () => {
  const WA_LINK = useGlobalWaLink();
  return (
  <div
    className="max-w-2xl mx-auto bg-[#0F0F0F] border border-[#333333] rounded-2xl p-10 text-center"
    data-testid="agenda-empty-state"
  >
    <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange">
      <iconify-icon icon="solar:calendar-search-linear" className="text-3xl"></iconify-icon>
    </div>
    <h3 className="text-xl md:text-2xl font-bold text-[#EAEAEA] mb-3">
      Nenhuma turma encontrada para os filtros selecionados
    </h3>
    <p className="text-sm text-neutral-400 mb-8 max-w-xl mx-auto">
      Novas turmas são abertas frequentemente. Fale com a equipe para saber as próximas datas ou
      solicitar uma turma In Company exclusiva.
    </p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="empty-cta-diagnosis"
        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-orange text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors"
      >
        <iconify-icon icon="solar:calendar-mark-linear" className="text-base"></iconify-icon>
        Agende seu diagnóstico
      </a>
      <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="empty-cta-specialist"
        className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#333333] text-[#EAEAEA] rounded-full text-xs font-bold uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors"
      >
        <iconify-icon icon="solar:phone-linear" className="text-base"></iconify-icon>
        Tire suas dúvidas com um especialista
      </a>
    </div>
  </div>
  );
};

export const ProgramShortcuts = ({ turmasByProgram, onPick }) => (
  <section
    className="relative py-16 border-t border-neutral-900"
    style={{ background: 'rgba(17, 17, 17, 0.78)' }}
  >
    <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
      <div className="text-center mb-10">
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
          Encontre por programa
        </span>
        <h2 className="text-2xl md:text-3xl font-bold text-[#EAEAEA]">Atalhos por programa</h2>
      </div>
      <div
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto pb-2"
        data-testid="program-shortcuts"
      >
        {/* PROGRAMS array imported in shared */}
        {Object.values(PROGRAM_BY_SLUG).map((p) => (
          <button
            key={p.slug}
            onClick={() => onPick(p.slug)}
            data-testid={`program-shortcut-${p.slug}`}
            className="text-left bg-[#0F0F0F] border border-[#333333] rounded-2xl p-5 hover:border-accent-orange transition-colors group"
          >
            <div className="w-11 h-11 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange mb-4">
              <iconify-icon icon={p.icon} className="text-xl"></iconify-icon>
            </div>
            <div className="font-bold text-[#EAEAEA] mb-1">{p.name}</div>
            <div className="text-xs text-neutral-500 mb-4">
              {turmasByProgram[p.slug] > 0
                ? `${turmasByProgram[p.slug]} turma${turmasByProgram[p.slug] > 1 ? 's' : ''} aberta${
                    turmasByProgram[p.slug] > 1 ? 's' : ''
                  }`
                : 'Em breve novas turmas'}
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-accent-orange group-hover:gap-2 transition-all">
              Ver turmas
              <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
            </span>
          </button>
        ))}
      </div>
    </div>
  </section>
);

export const FinalCTA = () => {
  const WA_LINK = useGlobalWaLink();
  return (
  <section
    className="relative py-20 border-t border-neutral-900"
    style={{ background: 'rgba(10, 10, 10, 0.78)' }}
  >
    <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-[#EAEAEA] mb-4">
        Não encontrou a turma ideal?
      </h2>
      <p className="text-base text-neutral-400 mb-8">
        Solicite uma turma In Company exclusiva para sua empresa ou fale com a equipe para
        orientação personalizada.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="agenda-cta-incompany"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors"
        >
          <iconify-icon icon="solar:buildings-3-linear" className="text-lg"></iconify-icon>
          Solicitar turma In Company
        </a>
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="agenda-cta-team"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#333333] text-[#EAEAEA] rounded-full text-sm font-bold uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors"
        >
          <iconify-icon icon="solar:chat-round-line-linear" className="text-lg"></iconify-icon>
          Falar com a equipe técnica
        </a>
      </div>
    </div>
  </section>
  );
};
